import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Spinner } from "@/components/shared";
import { aaLabel, AA_CATEGORIES } from "@/lib/askAnything";
import { Gauge, Clock, Smile, ListChecks } from "lucide-react";

function Metric({ icon: Icon, label, value, tint }) {
  return (
    <div className="kid-card p-4">
      <div className="w-9 h-9 rounded-xl grid place-items-center mb-2" style={{ background: tint }}><Icon size={17} className="text-white" /></div>
      <div className="text-2xl font-extrabold text-kid-navy font-display">{value}</div>
      <div className="text-xs text-kid-muted mt-0.5">{label}</div>
    </div>
  );
}

export function AskReportDialog({ open, onClose }) {
  const [r, setR] = useState(null);
  useEffect(() => { if (open) { setR(null); api.get("/admin/ask-anything/report").then((res) => setR(res.data)).catch(() => setR({ categories: [] })); } }, [open]);
  const maxCat = r?.categories?.[0]?.count || 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="aa-report-dialog">
        <DialogHeader>
          <DialogTitle>Ask Anything — reporting</DialogTitle>
          <DialogDescription>Volume, SLA adherence, categories and satisfaction.</DialogDescription>
        </DialogHeader>
        {!r ? <Spinner /> : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="aa-report-metrics">
              <Metric icon={ListChecks} label="Total conversations" value={r.total} tint="var(--teal)" />
              <Metric icon={Gauge} label="SLA adherence (72h)" value={`${r.sla_adherence}%`} tint="var(--green)" />
              <Metric icon={Clock} label="Avg first reply" value={`${r.avg_response_hours}h`} tint="var(--sky)" />
              <Metric icon={Smile} label="Satisfaction" value={r.satisfaction == null ? "—" : `${r.satisfaction}%`} tint="var(--grape)" />
            </div>

            <div>
              <h4 className="font-display font-bold text-kid-navy mb-2 text-sm">By category</h4>
              <div className="space-y-2" data-testid="aa-report-categories">
                {r.categories.map((c) => (
                  <div key={c.category} className="flex items-center gap-3">
                    <div className="w-40 text-xs text-kid-ink/80 truncate">{aaLabel(AA_CATEGORIES, c.category)}</div>
                    <div className="flex-1 h-2.5 bg-kid-mist rounded-full overflow-hidden"><div className="h-full bg-kid-teal rounded-full" style={{ width: `${(c.count / maxCat) * 100}%` }} /></div>
                    <div className="w-8 text-right text-xs font-semibold text-kid-navy">{c.count}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center" data-testid="aa-report-secondary">
              <div className="kid-card p-3"><div className="text-lg font-extrabold text-kid-navy">{r.helpful}</div><div className="text-xs text-kid-muted">Helpful votes</div></div>
              <div className="kid-card p-3"><div className="text-lg font-extrabold text-kid-navy">{r.not_helpful}</div><div className="text-xs text-kid-muted">Not helpful</div></div>
              <div className="kid-card p-3"><div className="text-lg font-extrabold text-kid-navy">{r.saved_to_roadmap}</div><div className="text-xs text-kid-muted">Saved to roadmap</div></div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
